import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Stack,
  CircularProgress, Alert, Divider, Avatar,
} from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { staffService } from '@smartgrow/shared';

const Row = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight="medium">{value ?? '—'}</Typography>
  </Box>
);

export const StaffDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const staffId = id!;

  const { data: member, isLoading, isError } = useQuery({
    queryKey: ['staff', staffId],
    queryFn: () => staffService.getStaffById(staffId),
    enabled: !!staffId,
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  if (isError || !member) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load staff member.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const initials = member.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">{member.fullName}</Typography>
          <Typography variant="caption" color="text.secondary">@{member.username}</Typography>
        </Box>
        <Chip label={member.active ? 'Active' : 'Inactive'} color={member.active ? 'success' : 'error'} size="small" />
        <Button startIcon={<Edit />} variant="outlined" size="small" onClick={() => navigate(`/dashboard/staff/${staffId}/edit`)}>
          Edit
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: 28, mx: 'auto', mb: 2 }}>
                {initials}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">{member.fullName}</Typography>
              <Chip label={member.role} color={member.role === 'ADMIN' ? 'primary' : 'default'} size="small" sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Account Details</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Username" value={member.username} />
              <Row label="Email" value={member.email} />
              <Row label="Role" value={member.role} />
              <Row label="Status" value={member.active ? 'Active' : 'Inactive'} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
